import { useEffect, useRef, useState, type ReactNode } from "react";
import { useAtomValue } from "jotai";
import { getBranding, getMyBranding, type Branding } from "@/api/branding";
import { useAuthStore } from "@/store/auth";
import { impersonationContextAtom } from "@/store/impersonation";
import { writeCachedBrand } from "@/contexts/brandCache";
import {
  BrandContext,
  DEFAULT_BRANDING,
  darkenHslTriplet,
  hexToHslTriplet,
} from "@/contexts/BrandContext";

/**
 * Sets a `<link rel="icon">` href, creating the element if absent. A `null`
 * value is a no-op (we never strip an existing favicon, preserving the default).
 */
function applyFavicon(faviconUrl: string | null): void {
  if (!faviconUrl) {
    return;
  }
  let link = document.querySelector(
    'link[rel~="icon"]',
  ) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = faviconUrl;
}

/**
 * Names of the brand CSS vars injected on `documentElement`. Includes the
 * DERIVED `--brand-primary-hover` (computed from the primary) so resetBrand
 * clears it too and the :root defaults take over again. Omitting a derived var
 * here would leave a previous dealer's value stranded on the element after
 * logout.
 */
const BRAND_VARS = [
  "--brand-primary",
  "--brand-primary-hover",
  "--brand-sidebar",
  "--brand-accent",
];

/**
 * Reverts the document to the platform defaults: removes the injected brand CSS
 * vars (so `src/index.css` values take over again) and restores the title.
 * Used when an authenticated dealer logs out and the brand must fall back to
 * the platform look. The favicon is intentionally left in place — the browser
 * keeps the last-set icon and there is no bundled default to restore to.
 */
function resetBrand(): void {
  const root = document.documentElement;
  for (const name of BRAND_VARS) {
    root.style.removeProperty(name);
  }
}

/**
 * Applies a resolved brand to the document: injects the three brand CSS vars
 * (primary/sidebar/accent) on `documentElement`, sets the favicon, and sets the
 * title. Only NON-empty fields are applied; empty fields leave the CSS/HTML
 * defaults in place. A `null`/default brand resets the previously applied vars.
 */
function applyBrand(brand: Branding | null): void {
  if (!brand || brand.is_default) {
    resetBrand();
    return;
  }
  const root = document.documentElement;
  const vars: Array<[string, string]> = [
    ["--brand-primary", brand.colors.primary],
    ["--brand-sidebar", brand.colors.sidebar],
    ["--brand-accent", brand.colors.accent],
  ];
  for (const [name, hex] of vars) {
    const triplet = hexToHslTriplet(hex);
    if (triplet) {
      root.style.setProperty(name, triplet);
      // The primary carries a paired, brand-derived hover shade so button hover
      // states track the active brand instead of a fixed blue.
      if (name === "--brand-primary") {
        const hover = darkenHslTriplet(triplet);
        if (hover) {
          root.style.setProperty("--brand-primary-hover", hover);
        }
      }
    }
  }
  if (brand.favicon_url) {
    applyFavicon(brand.favicon_url);
  }
  if (brand.brand_name) {
    document.title = brand.brand_name;
  }
}

interface Props {
  children: ReactNode;
  /**
   * Optional dealer slug to preview a specific brand (Phase A `?dealer=`).
   * Only used on the UNAUTHENTICATED public surface (login / tracking); an
   * authenticated user's brand is resolved server-side from their tenant.
   */
  dealerSlug?: string;
}

/**
 * Applies the resolved brand to the document and context. The brand SOURCE
 * depends on auth state (BUG-243):
 *
 *  - AUTHENTICATED users get their tenant → dealer brand from
 *    `GET /api/v1/branding/` ({@link getMyBranding}). Dealer/superadmin users
 *    have `tenant = null`, so that endpoint returns `is_default: true` for them
 *    automatically — no route/shell gating is needed.
 *  - UNAUTHENTICATED users (login, `/track/:token`) keep the Host-based public
 *    endpoint ({@link getBranding}, optionally `?dealer=<slug>`).
 *
 * The effect RE-RUNS when the EFFECTIVE identity changes — either `isAuthenticated`
 * flips, or the active impersonation session changes (BUG-248). A dispatcher is
 * branded immediately after login and reverts to the platform default on logout;
 * and when a superadmin/dealer starts impersonating a branded tenant, the brand
 * re-resolves to that tenant's dealer brand (and reverts to the actor's own brand
 * on exit) — all without a full reload. Impersonation start/exit only swaps the
 * `auth_token` (it never flips `isAuthenticated`), so we cannot key on auth state
 * alone; the {@link impersonationContextAtom} — set by the start/exit flows — is
 * the reactive signal that the effective (tenant) identity changed.
 *
 * StrictMode-safety (do NOT reintroduce BUG-238): instead of a one-shot ref, we
 * key the fetch on the current EFFECTIVE identity (auth state + impersonation
 * session) via `fetchedForKeyRef`. A StrictMode double-invoke sees the same key
 * and skips the second fetch, so the result is applied exactly once; a genuine
 * identity change (login/logout or impersonation start/exit) changes the key and
 * re-fetches. We deliberately do NOT use a per-run `cancelled` cleanup flag —
 * that was the bug that discarded the only in-flight fetch under StrictMode.
 * Never blocks first paint: children render with the platform default until a
 * dealer brand resolves; an `is_default` response or any error is a silent
 * no-op (default look preserved).
 */
export function BrandProvider({ children, dealerSlug }: Props) {
  const [branding, setBranding] = useState<Branding>(DEFAULT_BRANDING);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // The active impersonation session, updated by the start/exit flows. Its value
  // changes (context ⇄ null) on every impersonation transition even though
  // `isAuthenticated` stays true throughout, so it is the signal that the
  // effective (target-tenant) identity — and thus the brand — has changed.
  const impersonation = useAtomValue(impersonationContextAtom);
  // A stable string identifying the current effective identity + surface. The
  // fetch re-runs when this key changes; a StrictMode double-invoke sees the same
  // key and fetches exactly once (BUG-238-safe).
  const identityKey = [
    isAuthenticated ? "auth" : "anon",
    dealerSlug ?? "",
    impersonation
      ? // Key on the target's UNIQUE id so two same-named targets (or an empty
        // displayName) never collide on the same identity key; fall back to the
        // displayName only if `targetId` is somehow absent so nothing regresses.
        `imp:${impersonation.targetKind}:${
          impersonation.targetId ?? impersonation.displayName ?? ""
        }`
      : "own",
  ].join("|");
  // Tracks which identity we have already fetched for, so a StrictMode
  // double-invoke fetches once, while a real identity change re-fetches.
  const fetchedForKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (fetchedForKeyRef.current === identityKey) {
      return;
    }
    fetchedForKeyRef.current = identityKey;
    const fetchBrand = isAuthenticated
      ? getMyBranding()
      : getBranding(dealerSlug);
    void fetchBrand
      .then((brand) => {
        applyBrand(brand);
        setBranding(brand.is_default ? DEFAULT_BRANDING : brand);
        // Persist for pre-paint application on the next reload (BUG-245); an
        // is_default brand clears the host's cache inside writeCachedBrand.
        writeCachedBrand(brand);
      })
      .catch(() => {
        // Branding is best-effort: on any failure keep the platform default.
      });
  }, [identityKey, isAuthenticated, dealerSlug]);

  return (
    <BrandContext.Provider value={branding}>{children}</BrandContext.Provider>
  );
}
